import {forwardRef} from 'react';

import {cn} from '../../lib/utils';
import {Icon} from '../Icon';

import type {IconName} from '../Icon';
import type {ComponentProps} from 'react';

export interface IconBoxProps extends Omit<ComponentProps<'div'>, 'children'> {
  icon: IconName;
  size?: IconBoxSize;
  variant?: 'surface' | 'secondary' | 'tertiary' | 'brand';
  tone?: IconBoxTone;
  shape?: 'square' | 'round';
  shadow?: boolean;
  iconColor?: 'secondary' | 'tertiary' | 'brand';
  iconClassName?: string;
}

export const iconBoxSizeConfig = {
  xs: {box: 'size-6.5 rounded-md', iconSize: 14},
  sm: {box: 'size-8 rounded-md', iconSize: 16},
  md: {box: 'size-10 rounded-md', iconSize: 20}
} as const;

export type IconBoxSize = keyof typeof iconBoxSizeConfig;

const variantConfig = {
  surface: {className: 'bg-background-surface', border: true},
  secondary: {className: 'bg-background-secondary', border: true},
  tertiary: {className: 'bg-background-tertiary', border: false},
  brand: {className: 'bg-brand-primary/[6%]', border: false}
} as const;

export const iconBoxToneConfig = {
  neutral: {box: 'bg-background-secondary', icon: 'text-fg-secondary'},
  brand: {box: 'bg-background-selected', icon: 'text-brand-primary'},
  success: {box: 'bg-background-success', icon: 'text-text-success'},
  warning: {box: 'bg-background-warning', icon: 'text-text-warning'},
  purple: {box: 'bg-background-purple', icon: 'text-text-purple'}
} as const;

export type IconBoxTone = keyof typeof iconBoxToneConfig;

const iconColorConfig = {
  secondary: 'text-fg-secondary',
  tertiary: 'text-fg-tertiary',
  brand: 'text-brand-primary'
} as const;

export const IconBox = forwardRef<HTMLDivElement, IconBoxProps>(function IconBox(
  {
    icon,
    size = 'md',
    variant = 'surface',
    tone,
    shape = 'square',
    shadow = false,
    iconColor,
    iconClassName,
    className,
    ...props
  },
  ref
) {
  const {box, iconSize} = iconBoxSizeConfig[size];
  // Off by default for the tinted tertiary/brand variants, which read as flat tiles.
  const {className: variantClassName, border} = variantConfig[variant];
  const toneStyle = tone && iconBoxToneConfig[tone];

  return (
    <div
      ref={ref}
      data-slot="icon-box"
      className={cn(
        'flex shrink-0 items-center justify-center',
        !toneStyle && border && 'border border-border',
        box,
        shape === 'round' && 'rounded-full',
        toneStyle ? toneStyle.box : variantClassName,
        shadow && 'shadow-sm',
        className
      )}
      {...props}
    >
      <Icon
        name={icon}
        width={iconSize}
        height={iconSize}
        className={cn(
          toneStyle ? toneStyle.icon : iconColor && iconColorConfig[iconColor],
          iconClassName
        )}
      />
    </div>
  );
});
